import { useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { useAppWindowFlowShellStore } from 'src/app/router/layouts/navigation/app-window-frame-business'
import { paths } from 'src/config/paths'
import { useCreateBusiness } from 'src/features/business-create/api/use-create-business'
import { BusinessCreateContext } from 'src/features/business-create/context/business-create-context'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useToast } from 'src/shared/hooks/use-toast'
import { cn } from 'src/shared/utils/cn'

import BCFooter from '../components/bc-footer'
import BCHeader from '../components/bc-header'
import BusinessCreateNavbar from '../components/bc-navbar'
import type { BCStepName } from '../constants/bc-config'
import type { BusinessCreateFooterConfig } from '../context/business-create-context'
import useCreateBusinessConfig from '../hooks/use-create-business-config'
import useDissolveScreen from '../hooks/use-dissolve-screen'
import useBusinessAdditionalInfoStore from '../store/business-additional-info-store'
import { useBusinessCreateStore } from '../store/business-create-store'

export default function BusinessCreateLayout() {
  const { isMobile } = useBreakpoint()
  const { config, totalSteps } = useCreateBusinessConfig()
  const { step } = useParams()
  const { reset, getFormData } = useBusinessCreateStore()
  const { setFlowShell, clearFlowShell } = useAppWindowFlowShellStore()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { mutate: createBusiness } = useCreateBusiness()
  const { setDissolve, dissolveStyles, dissolveDuration } = useDissolveScreen()
  const [isLoading, setIsLoading] = useState(false)
  const [footerConfig, setFooterConfig] = useState<BusinessCreateFooterConfig | null>(null)
  const stepKeys = useMemo(() => Object.keys(config.steps) as BCStepName[], [config.steps])
  const currentStepKey = step as BCStepName | undefined
  const currentStepIndex = useMemo(
    () => (currentStepKey ? stepKeys.findIndex((stepKey) => stepKey === currentStepKey) : -1),
    [currentStepKey, stepKeys],
  )
  const { selectedInfoType, setSelectedInfoType } = useBusinessAdditionalInfoStore()

  useEffect(() => {
    return () => {
      reset()
    }
  }, [])

  useEffect(() => {
    if (isMobile) {
      clearFlowShell()
      return
    }
    setFlowShell({
      content: <BusinessCreateNavbar />,
      width: 100,
      headerHeight: 20,
    })
  }, [clearFlowShell, isMobile, setFlowShell])

  useEffect(() => {
    return () => {
      clearFlowShell()
    }
  }, [clearFlowShell])

  const handleBack = useCallback(() => {
    if (selectedInfoType) {
      setSelectedInfoType(null)
      return
    }
    setDissolve(true)
    setTimeout(() => {
      navigate(-1)
      setDissolve(false)
    }, dissolveDuration)
  }, [dissolveDuration, navigate, setDissolve, setSelectedInfoType, selectedInfoType])

  const handleNext = useCallback(
    (skip = 0) => {
      // navigate(paths.business.path, {
      //   state: { type: 'createSuccess', business: { id: 1 }, coordinates: { lat: 0, lng: 0 } },
      //   replace: true,
      //   flushSync: true,
      // })
      // return
      const formData = getFormData()
      setDissolve(true)

      const shouldSkipClosedDate = currentStepKey === 'is-closed' && formData.isClosed === false
      const targetStepIndex = currentStepIndex + 1 + skip + (shouldSkipClosedDate ? 1 : 0)

      if (targetStepIndex >= stepKeys.length || targetStepIndex >= totalSteps) {
        setIsLoading(true)

        // Get the coordinates from the first provided location.
        let coordinates: { lat: number; lng: number } | undefined
        const exactLocations = formData?.exactLocations
        const desiredLocations = formData?.desiredLocations
        if (exactLocations && exactLocations.length > 0) {
          coordinates = {
            lat: exactLocations[0].latitude,
            lng: exactLocations[0].longitude,
          }
        } else if (desiredLocations && desiredLocations.length > 0) {
          coordinates = {
            lat: desiredLocations[0].latitude,
            lng: desiredLocations[0].longitude,
          }
        }

        createBusiness(formData || {}, {
          onSuccess: (response) => {
            navigate(paths.business.path, {
              state: { type: 'createSuccess', business: response, coordinates },
            })
          },
          onError: () => {
            toast({
              variant: 'destructive',
              title: 'Error',
              description: 'Failed to create business',
            })
          },
          onSettled: () => {
            setIsLoading(false)
          },
        })
        return
      }

      setTimeout(() => {
        const nextStepKey = stepKeys[targetStepIndex]
        navigate(paths.business.create.getHref(nextStepKey))
        setDissolve(false)
      }, dissolveDuration)
    },
    [
      createBusiness,
      currentStepKey,
      currentStepIndex,
      dissolveDuration,
      getFormData,
      navigate,
      setDissolve,
      stepKeys,
      toast,
      totalSteps,
    ],
  )

  const handleGoToStep = useCallback(
    (targetStep: BCStepName) => {
      const targetStepIndex = stepKeys.findIndex((stepKey) => stepKey === targetStep)
      if (targetStepIndex < 0 || targetStepIndex >= totalSteps) return

      setDissolve(true)
      setTimeout(() => {
        navigate(paths.business.create.getHref(targetStep))
        setDissolve(false)
      }, dissolveDuration)
    },
    [dissolveDuration, navigate, setDissolve, stepKeys, totalSteps],
  )

  const contextValue = useMemo(
    () => ({ handleNext, handleGoToStep, handleBack, setFooterConfig }),
    [handleBack, handleGoToStep, handleNext],
  )

  if (isMobile) {
    return (
      <div className={cn('size-full')} style={dissolveStyles}>
        <Outlet />
      </div>
    )
  }

  return (
    <BusinessCreateContext.Provider value={contextValue}>
      <div className="relative size-full">
        <BCHeader />
        <div className={cn('flex size-full pb-[100px]')} style={dissolveStyles}>
          <Outlet />
        </div>
        <BCFooter
          isLoading={isLoading}
          buttonText={footerConfig?.buttonText ?? 'Continue'}
          buttonVariant={footerConfig?.buttonVariant ?? 'default'}
          primaryAction={footerConfig?.primaryAction}
          secondaryButton={footerConfig?.secondaryButton}
        />
      </div>
    </BusinessCreateContext.Provider>
  )
}
